import {
  BackendError,
  databaseError,
  sortFiles,
  type CompleteResult,
  type DepositBackend,
  type DepositDetail,
  type DepositEventRow,
  type DepositFileRow,
  type DepositRow,
  type DepositSummary,
  type DuplicateCheck,
  type Member,
  type MissingObject,
  type StartResult,
  type UploadOutcome,
} from "./backend.js";
import { contentFingerprint, sha256Hex } from "./hash.js";
import { parseInstagramUrl } from "./instagram.js";
import type { DepositManifest } from "./prepare.js";

interface MemoryDeposit extends DepositRow {
  client_key: string;
  zip_sha256: string | null;
  content_fingerprint: string;
  instagram_shortcode: string | null;
}

interface MemoryFile extends DepositFileRow {
  integrity_detail: string | null;
}

const STORED_TYPES = ["image/png", "image/jpeg", "image/webp", "application/zip", "text/plain", "text/markdown", "application/json", "text/csv", "application/pdf"];
const SHA = /^[0-9a-f]{64}$/;

/**
 * The deposit backend in memory: the web demo (fictitious data, nothing leaves
 * the browser) and unit tests. It follows the SQL functions of
 * supabase/migrations/20261009090000_carousel_deposits.sql; the shared contract
 * tests (src/testing/contract.ts) run against both.
 */
export class MemoryStore {
  readonly members = new Map<string, Member>();
  readonly deposits: MemoryDeposit[] = [];
  readonly files: MemoryFile[] = [];
  readonly events: DepositEventRow[] = [];
  readonly objects = new Map<string, { data: Uint8Array; mediaType: string }>();
  fileSizeLimit = 52428800;
  private numbers = 0;
  private eventIds = 0;

  constructor(readonly now: () => Date = () => new Date()) {}

  addMember(userId: string, displayName: string): void {
    this.members.set(userId, { user_id: userId, display_name: displayName });
  }

  backend(userId: string | null): MemoryDepositBackend {
    return new MemoryDepositBackend(this, userId);
  }

  nextNumber(): number {
    this.numbers += 1;
    return this.numbers;
  }

  event(depositId: string, actor: string | null, kind: string, detail: Record<string, unknown> = {}): void {
    this.eventIds += 1;
    this.events.push({ id: this.eventIds, deposit_id: depositId, at: this.now().toISOString(), actor, kind, detail });
  }

  missingFiles(depositId: string): MissingObject[] {
    const missing = new Map<string, MissingObject>();
    for (const f of this.files.filter((x) => x.deposit_id === depositId)) {
      const stored = this.objects.get(f.storage_path);
      if (stored && stored.data.length === f.bytes) continue;
      missing.set(f.storage_path, { storage_path: f.storage_path, sha256: f.sha256, bytes: f.bytes, media_type: f.media_type, conflict: stored !== undefined });
    }
    return [...missing.values()];
  }

  /** public.refresh_deposit_integrity */
  refreshIntegrity(depositId: string, detail: Record<string, unknown>): string | null {
    const d = this.deposits.find((x) => x.id === depositId);
    if (!d || d.upload_status !== "complete") return null;
    const files = this.files.filter((f) => f.deposit_id === depositId);
    const next = files.some((f) => f.integrity === "mismatch" || f.integrity === "missing")
      ? "failed"
      : files.every((f) => f.integrity === "verified")
        ? "verified"
        : "pending";
    if (next === d.integrity_status) return null;
    d.integrity_status = next;
    d.integrity_checked_at = d.updated_at = this.now().toISOString();
    if (next !== "pending") this.event(d.id, null, next === "verified" ? "integrity_verified" : "integrity_failed", detail);
    return next;
  }

  /** What the service-side verifier does (apps/deposit-ops): hash the pending objects of saved deposits. */
  async verifyPending(): Promise<{ checked: number; failed: string[] }> {
    const saved = new Set(this.deposits.filter((d) => d.upload_status === "complete").map((d) => d.id));
    const paths = [...new Set(this.files.filter((f) => f.integrity === "pending" && saved.has(f.deposit_id)).map((f) => f.storage_path))];
    const failed: string[] = [];
    for (const path of paths) {
      const stored = this.objects.get(path);
      const ok = stored !== undefined && `files/${await sha256Hex(stored.data)}` === path;
      if (!ok) failed.push(path);
      this.recordIntegrity(path, ok, ok ? null : stored ? "sha256 mismatch" : "missing");
    }
    return { checked: paths.length, failed };
  }

  /** public.record_object_integrity */
  recordIntegrity(path: string, ok: boolean, detail: string | null): number {
    const at = this.now().toISOString();
    const deposits = new Set<string>();
    for (const f of this.files.filter((x) => x.storage_path === path)) {
      f.integrity = ok ? "verified" : detail === "missing" ? "missing" : "mismatch";
      f.integrity_detail = detail;
      f.verified_at = at;
      deposits.add(f.deposit_id);
    }
    return [...deposits].filter((id) => this.refreshIntegrity(id, { path, detail }) !== null).length;
  }
}

const summary = (d: MemoryDeposit): DepositSummary => ({
  id: d.id,
  number: d.number,
  upload_status: d.upload_status,
  integrity_status: d.integrity_status,
  analysis_status: d.analysis_status,
  slide_count: d.slide_count,
  created_at: d.created_at,
  created_by: d.created_by,
  version: d.version,
});

const copy = <T>(value: T): T => structuredClone(value);

export class MemoryDepositBackend implements DepositBackend {
  constructor(
    private readonly store: MemoryStore,
    private readonly userId: string | null,
  ) {}

  private member(): string {
    if (this.userId === null || !this.store.members.has(this.userId)) throw databaseError("42501", "not_a_member");
    return this.userId;
  }

  private deposit(id: string): MemoryDeposit {
    const d = this.store.deposits.find((x) => x.id === id);
    if (!d) throw databaseError("P0002", "deposit_not_found");
    return d;
  }

  async currentMember(): Promise<Member | null> {
    const member = this.userId === null ? undefined : this.store.members.get(this.userId);
    return member ? copy(member) : null;
  }

  async findDuplicates(zipSha256: string | null, imageSha256: string[]): Promise<DuplicateCheck> {
    this.member();
    const fingerprint = await contentFingerprint(imageSha256);
    const exact = this.store.deposits.find((d) => (zipSha256 !== null && d.zip_sha256 === zipSha256) || d.content_fingerprint === fingerprint) ?? null;
    const shared = new Map<string, Set<string>>();
    for (const f of this.store.files) {
      if ((f.role === "slide" || f.role === "extra_image") && imageSha256.includes(f.sha256) && f.deposit_id !== exact?.id) {
        shared.set(f.deposit_id, (shared.get(f.deposit_id) ?? new Set()).add(f.sha256));
      }
    }
    const overlaps = [...shared].map(([id, set]) => ({ id, number: this.deposit(id).number, shared_images: set.size })).sort((a, b) => a.number - b.number);
    return { content_fingerprint: fingerprint, exact: exact ? summary(exact) : null, overlaps };
  }

  async startDeposit(clientKey: string, manifest: DepositManifest): Promise<StartResult> {
    const uid = this.member();
    const kind = manifest.source_kind;
    const files = manifest.files;
    const problems: string[] = [];
    if (kind !== "zip" && kind !== "images") throw databaseError("22023", "invalid_manifest: source_kind");
    if (!files.length) throw databaseError("22023", "invalid_manifest: files");
    if (files.length > 300) throw databaseError("22023", "invalid_manifest: too many files");
    for (const f of files) {
      if (!SHA.test(f.sha256)) problems.push(`bad sha256 for ${f.source_path}`);
      if (!(f.bytes > 0)) problems.push(`bad size for ${f.source_path}`);
      if (f.bytes > this.store.fileSizeLimit) problems.push(`${f.source_path} is larger than ${this.store.fileSizeLimit} bytes`);
      if (kind === "images" && (f.role === "original_zip" || f.origin !== "uploaded_file")) problems.push("an images deposit holds uploaded files only");
      if (kind === "zip" && f.role !== "original_zip" && f.origin !== "zip_entry") problems.push("files of a ZIP deposit come from the ZIP");
    }
    if (kind === "zip" && files.filter((f) => f.role === "original_zip").length !== 1) problems.push("a ZIP deposit has exactly one original_zip");
    const slides = files.filter((f) => f.role === "slide");
    if (slides.length < 1 || slides.length > 20) problems.push(`${slides.length} slides (1 to 20 expected)`);
    const positions = slides.map((f) => f.position ?? 0).sort((a, b) => a - b);
    if (positions.some((p, i) => p !== i + 1)) problems.push("slide positions must be 1..n");
    if (problems.length) throw databaseError("22023", `invalid_manifest: ${problems.join("; ")}`);

    const zipHash = files.find((f) => f.role === "original_zip")?.sha256 ?? null;
    const fingerprint = await contentFingerprint(files.filter((f) => f.role === "slide" || f.role === "extra_image").map((f) => f.sha256));

    const retry = this.store.deposits.find((d) => d.client_key === clientKey);
    if (retry) {
      if (retry.content_fingerprint !== fingerprint) throw databaseError("22023", "client_key_reused");
      return { outcome: "existing", deposit: summary(retry), missing: this.store.missingFiles(retry.id) };
    }
    const same = this.store.deposits
      .filter((d) => (zipHash !== null && d.zip_sha256 === zipHash) || d.content_fingerprint === fingerprint)
      .sort((a, b) => a.number - b.number)[0];
    if (same) {
      if (same.upload_status === "uploading") {
        this.store.event(same.id, uid, "resumed", { client_key: clientKey });
        return { outcome: "resume", deposit: summary(same), missing: this.store.missingFiles(same.id) };
      }
      return { outcome: "duplicate", deposit: summary(same), missing: [] };
    }
    const at = this.store.now().toISOString();
    const d: MemoryDeposit = {
      id: globalThis.crypto.randomUUID(),
      number: this.store.nextNumber(),
      client_key: clientKey,
      created_by: uid,
      created_at: at,
      updated_at: at,
      source_kind: kind,
      zip_sha256: zipHash,
      content_fingerprint: fingerprint,
      slide_count: slides.length,
      upload_status: "uploading",
      completed_at: null,
      integrity_status: "pending",
      integrity_checked_at: null,
      analysis_status: "not_analyzed",
      note: manifest.note?.trim() || null,
      instagram_url: null,
      instagram_shortcode: null,
      published_at: null,
      version: 1,
    };
    this.store.deposits.push(d);
    for (const f of files) {
      this.store.files.push({
        id: globalThis.crypto.randomUUID(),
        deposit_id: d.id,
        role: f.role,
        origin: f.origin,
        source_path: f.source_path,
        position: f.position,
        sha256: f.sha256,
        bytes: f.bytes,
        media_type: f.media_type,
        storage_path: `files/${f.sha256}`,
        integrity: "pending",
        integrity_detail: null,
        verified_at: null,
      });
    }
    this.store.event(d.id, uid, "started", { source_kind: kind, slides: slides.length, files: files.length });
    return { outcome: "created", deposit: summary(d), missing: this.store.missingFiles(d.id) };
  }

  async uploadObject(storagePath: string, data: Uint8Array, mediaType: string): Promise<UploadOutcome> {
    this.member();
    if (!/^files\/[0-9a-f]{64}$/.test(storagePath)) throw new BackendError("invalid", "Chemin de stockage refusé.");
    if (!STORED_TYPES.includes(mediaType)) throw new BackendError("invalid", `Type ${mediaType} refusé par le stockage.`);
    if (data.length > this.store.fileSizeLimit) throw new BackendError("invalid", "Fichier plus lourd que la limite du stockage.");
    if (this.store.objects.has(storagePath)) return "exists";
    this.store.objects.set(storagePath, { data: data.slice(), mediaType });
    return "created";
  }

  async completeDeposit(depositId: string): Promise<CompleteResult> {
    const uid = this.member();
    const d = this.deposit(depositId);
    if (d.upload_status === "complete") return { outcome: "complete", deposit: summary(d), missing: [] };
    const missing = this.store.missingFiles(d.id);
    if (missing.length) return { outcome: "incomplete", deposit: summary(d), missing };
    d.upload_status = "complete";
    d.completed_at = d.updated_at = this.store.now().toISOString();
    d.version += 1;
    this.store.event(d.id, uid, "completed");
    this.store.refreshIntegrity(d.id, {});
    return { outcome: "complete", deposit: summary(d), missing: [] };
  }

  async listDeposits(): Promise<DepositRow[]> {
    this.member();
    return copy([...this.store.deposits].sort((a, b) => b.number - a.number));
  }

  async listMembers(): Promise<Member[]> {
    this.member();
    return copy([...this.store.members.values()]);
  }

  async getDeposit(depositId: string): Promise<DepositDetail> {
    this.member();
    const deposit = this.deposit(depositId);
    return copy({
      deposit,
      files: sortFiles(this.store.files.filter((f) => f.deposit_id === depositId)),
      events: this.store.events.filter((e) => e.deposit_id === depositId),
    });
  }

  async firstSlides(depositIds: string[]): Promise<Map<string, DepositFileRow>> {
    this.member();
    return new Map(this.store.files.filter((f) => f.role === "slide" && f.position === 1 && depositIds.includes(f.deposit_id)).map((f) => [f.deposit_id, copy(f)]));
  }

  async setSlideOrder(depositId: string, expectedVersion: number, slideIds: string[], extraIds: string[]): Promise<DepositSummary> {
    const uid = this.member();
    const d = this.deposit(depositId);
    if (d.upload_status !== "complete") throw databaseError("55000", "deposit_not_complete");
    if (d.version !== expectedVersion) throw databaseError("40001", `version_conflict: ${d.version}`);
    if (slideIds.length < 1 || slideIds.length > 20) throw databaseError("22023", "invalid_order: 1 to 20 slides");
    const images = this.store.files.filter((f) => f.deposit_id === d.id && (f.role === "slide" || f.role === "extra_image"));
    const listed = [...slideIds, ...extraIds].sort();
    if (listed.join() !== images.map((f) => f.id).sort().join()) throw databaseError("22023", "invalid_order: every image of the deposit must be listed once");
    const before = images.filter((f) => f.role === "slide").sort((a, b) => a.position! - b.position!).map((f) => f.source_path);
    for (const f of images) {
      const index = slideIds.indexOf(f.id);
      f.role = index >= 0 ? "slide" : "extra_image";
      f.position = index >= 0 ? index + 1 : null;
    }
    d.slide_count = slideIds.length;
    d.version += 1;
    d.updated_at = this.store.now().toISOString();
    const after = slideIds.map((id) => images.find((f) => f.id === id)!.source_path);
    this.store.event(d.id, uid, "order_changed", { before, after });
    return summary(d);
  }

  async linkInstagram(depositId: string, expectedVersion: number, url: string | null, publishedAt: string | null): Promise<DepositSummary> {
    const uid = this.member();
    const d = this.deposit(depositId);
    if (d.upload_status !== "complete") throw databaseError("55000", "deposit_not_complete");
    if (d.version !== expectedVersion) throw databaseError("40001", `version_conflict: ${d.version}`);
    if (url === null || !url.trim()) {
      if (publishedAt !== null) throw databaseError("22023", "invalid_link: a date needs a link");
      Object.assign(d, { instagram_url: null, instagram_shortcode: null, published_at: null });
      d.version += 1;
      d.updated_at = this.store.now().toISOString();
      this.store.event(d.id, uid, "instagram_unlinked");
      return summary(d);
    }
    let link: { url: string; shortcode: string };
    try {
      link = parseInstagramUrl(url);
    } catch {
      throw databaseError("22023", "invalid_link: not an Instagram post URL");
    }
    if (publishedAt !== null && new Date(publishedAt).getTime() > this.store.now().getTime() + 5 * 60_000) {
      throw databaseError("22023", "invalid_link: published_at is in the future");
    }
    const other = this.store.deposits.find((x) => x.instagram_shortcode === link.shortcode && x.id !== d.id);
    if (other) throw databaseError("23505", `instagram_already_linked: ${other.number}`);
    const published = publishedAt === null ? null : new Date(publishedAt).toISOString();
    Object.assign(d, { instagram_url: link.url, instagram_shortcode: link.shortcode, published_at: published });
    d.version += 1;
    d.updated_at = this.store.now().toISOString();
    this.store.event(d.id, uid, "instagram_linked", { url: link.url, published_at: published });
    return summary(d);
  }

  async objectUrl(storagePath: string, downloadName?: string): Promise<string> {
    this.member();
    const stored = this.store.objects.get(storagePath);
    if (!stored) throw databaseError("P0002", "object_not_found");
    // a blob: URL is same-origin, so <a download> names the file itself: downloadName is not needed
    void downloadName;
    return URL.createObjectURL(new Blob([stored.data as Uint8Array<ArrayBuffer>], { type: stored.mediaType }));
  }
}
